import { ArrowLeft, MapPin, Clock, Euro, Building2, MessageSquare, Send, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const JobDetail = () => {
  const [responseMessage, setResponseMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);
  const navigate = useNavigate();

  // Mock job data - would come from props/API in real app
  const job = {
    id: "1",
    title: "Mindfulness & Wellness Coach",
    industry: "Coaching",
    timeType: "Part-time",
    location: {
      type: "Hybrid", // Online, Physical, Hybrid
      details: "Amsterdam, Netherlands + Remote"
    },
    compensation: {
      amount: "€35-45",
      period: "per hour",
      benefits: ["Flexible schedule", "Professional development", "Health insurance"]
    },
    company: {
      name: "Conscious Living Collective",
      avatar: "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=200",
      verified: true,
      description: "A community-focused wellness center dedicated to holistic healing and personal growth."
    },
    postedDate: "2 days ago",
    applicants: 12,
    description: `We are seeking a passionate and experienced Mindfulness & Wellness Coach to join our conscious community. This is a perfect opportunity for someone who wants to make a meaningful impact while working flexible hours.

**What you'll do:**
• Conduct one-on-one coaching sessions with clients
• Lead group mindfulness workshops and meditation sessions  
• Develop personalized wellness plans for individual clients
• Collaborate with our holistic health team
• Maintain client progress documentation

**What we're looking for:**
• Certified mindfulness or wellness coach (2+ years experience)
• Strong communication and empathy skills
• Experience with meditation and stress management techniques
• Passionate about conscious living and personal growth
• Available for evening and weekend sessions

**Why join us:**
• Work with like-minded conscious individuals
• Flexible scheduling that fits your lifestyle
• Opportunity to grow your practice in a supportive environment
• Access to continuing education and workshops
• Collaborative and nurturing work culture

This role is perfect for someone who values work-life balance and wants to help others on their wellness journey. You'll have the freedom to create your own schedule while being part of a supportive community.`,
    requirements: [
      "Certified mindfulness or wellness coach",
      "2+ years of coaching experience",
      "Strong interpersonal skills",
      "Flexible schedule availability",
      "Passion for holistic wellness"
    ]
  };

  const handleRespondToJob = async () => {
    if (!responseMessage.trim()) {
      alert("Please write a message before responding");
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log("Job response submitted:", {
        jobId: job.id,
        message: responseMessage
      });
      
      setIsSubmitting(false);
      setHasResponded(true);
      
      // TODO: Submit response to database
      // TODO: Notify job poster
    }, 1500);
  };

  const handleGoBack = () => {
    console.log("Going back to previous page");
    navigate(-1);
  };

  const getLocationIcon = () => {
    switch (job.location.type) {
      case "Online":
        return "🌐";
      case "Physical":
        return "🏢";
      case "Hybrid":
        return "🔄";
      default:
        return "📍";
    }
  };

  // Success screen after responding
  if (hasResponded) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#f6f1eb' }}>
        <Card className="w-full max-w-md border-0 shadow-xl bg-white/90 backdrop-blur-sm" style={{ borderColor: '#efe7dd' }}>
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#efe7dd' }}>
                <Send className="h-8 w-8" style={{ color: '#b6a092' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#837069' }}>
                Response Sent!
              </h2>
              <p style={{ color: '#a0897c' }}>
                Your application has been submitted successfully. The employer will review your response and get back to you soon.
              </p>
            </div>

            <div className="rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: '#efe7dd' }}>
              <h3 className="font-semibold mb-2" style={{ color: '#837069' }}>What happens next:</h3>
              <div className="space-y-1 text-sm" style={{ color: '#a0897c' }}>
                <p>• The employer will review your application</p>
                <p>• You'll receive a notification if they're interested</p>
                <p>• Check your messages for direct communication</p>
              </div>
            </div>

            <Button 
              onClick={handleGoBack}
              className="w-full text-white"
              style={{ backgroundColor: '#b6a092' }}
            >
              Browse More Jobs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm" style={{ borderBottomColor: '#efe7dd', borderBottomWidth: '1px' }}>
        <div className="flex items-center justify-between px-6 py-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 font-medium hover:shadow-md transition-all"
            style={{ 
              backgroundColor: '#fbf8f4',
              borderColor: '#efe7dd',
              color: '#837069'
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Terug</span>
          </Button>
          <h1 className="font-semibold" style={{ color: '#837069' }}>Job Details</h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="px-6 py-6 pb-32">
        {/* Company Info Card */}
        <Card className="mb-6 border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ borderColor: '#efe7dd' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={job.company.avatar} alt={job.company.name} />
                <AvatarFallback style={{ backgroundColor: '#efe7dd', color: '#a0897c' }} className="text-lg">
                  {job.company.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold" style={{ color: '#837069' }}>{job.company.name}</h3>
                  {job.company.verified && (
                    <Badge variant="secondary" className="text-xs" style={{ 
                      backgroundColor: '#efe7dd', 
                      color: '#a0897c' 
                    }}>
                      Verified
                    </Badge>
                  )}
                </div>
                <p className="text-sm" style={{ color: '#b6a092' }}>{job.company.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm" style={{ color: '#b6a092' }}>
              <span>Posted {job.postedDate}</span>
              <span>•</span>
              <span>{job.applicants} applicants</span>
            </div>
          </CardContent>
        </Card>

        {/* Job Details Card */}
        <Card className="mb-6 border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ borderColor: '#efe7dd' }}>
          <CardContent className="p-6">
            {/* Job Title */}
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#837069' }}>
              {job.title}
            </h2>

            {/* Job Meta Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Industry & Time Type */}
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" style={{ color: '#b6a092' }} />
                <span style={{ color: '#a0897c' }}>
                  {job.timeType} – {job.industry}
                </span>
              </div>

              {/* Location */}
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" style={{ color: '#b6a092' }} />
                <span style={{ color: '#a0897c' }}>
                  {getLocationIcon()} {job.location.details}
                </span>
              </div>
            </div>

            {/* Compensation */}
            <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: '#efe7dd' }}>
              <div className="flex items-center gap-2 mb-2">
                <Euro className="h-5 w-5" style={{ color: '#b6a092' }} />
                <span className="font-semibold" style={{ color: '#837069' }}>Compensation</span>
              </div>
              <p className="text-xl font-bold mb-2" style={{ color: '#837069' }}>
                {job.compensation.amount} {job.compensation.period}
              </p>
              <div className="flex flex-wrap gap-2">
                {job.compensation.benefits.map((benefit, index) => (
                  <Badge key={index} variant="secondary" className="text-xs" style={{ 
                    backgroundColor: '#e6dbd0', 
                    color: '#a0897c' 
                  }}>
                    {benefit}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator className="my-6" style={{ backgroundColor: '#efe7dd' }} />

            {/* Full Description */}
            <div className="mb-6">
              <h4 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#837069' }}>
                <Briefcase className="h-5 w-5" />
                Job Description
              </h4>
              <div className="prose prose-sage max-w-none">
                {job.description.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h5 key={index} className="font-semibold mt-4 mb-2" style={{ color: '#837069' }}>
                        {paragraph.replace(/\*\*/g, '')}
                      </h5>
                    );
                  } else if (paragraph.startsWith('•')) {
                    return (
                      <p key={index} className="ml-4 mb-1" style={{ color: '#a0897c' }}>
                        {paragraph}
                      </p>
                    );
                  } else if (paragraph.trim()) {
                    return (
                      <p key={index} className="mb-3 leading-relaxed" style={{ color: '#a0897c' }}>
                        {paragraph}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            </div>

            {/* Requirements */}
            <div>
              <h4 className="font-semibold mb-3" style={{ color: '#837069' }}>Key Requirements:</h4>
              <div className="space-y-2">
                {job.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#b6a092' }} />
                    <span className="text-sm" style={{ color: '#a0897c' }}>{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Form */}
        <Card className="mb-6 border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ borderColor: '#efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2" style={{ color: '#837069' }}>
              <MessageSquare className="h-5 w-5" />
              Respond to this job posting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="response" className="font-medium" style={{ color: '#a0897c' }}>
                Your message to the employer
              </Label>
              <Textarea 
                id="response"
                placeholder="Tell them why you're interested in this role and what makes you a great fit..."
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                className="min-h-[120px]"
                style={{ borderColor: '#efe7dd' }}
              />
              <p className="text-xs" style={{ color: '#b6a092' }}>
                Include your relevant experience, availability, and why you're passionate about this opportunity.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Response Section */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t p-6" style={{ borderColor: '#efe7dd' }}>
        <div className="max-w-md mx-auto">
          <Button 
            onClick={handleRespondToJob}
            disabled={!responseMessage.trim() || isSubmitting}
            className="w-full text-white py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            style={{ backgroundColor: '#b6a092' }}
            size="lg"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Sending response...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Respond to this job posting
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;